import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCatalogStructure } from '../api';

function TopicsPage({ onTopicClick }) {
  const [searchParams] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(searchParams.get('topic') || '');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCatalogStructure()
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load catalog');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const filteredTopics = useMemo(() => {
    if (!catalog) return [];
    const q = search.trim().toLowerCase();
    const topics = catalog.topics || [];
    if (!q) return topics;
    return topics.filter((t) => t.name.toLowerCase().includes(q));
  }, [catalog, search]);

  const handlePracticeClick = (topic) => {
    if (!(topic.questionCount > 0)) return;
    onTopicClick(
      {
        state: topic.state,
        gradeLevel: topic.gradeLevel,
        subTopic: topic.name,
      },
      topic.name
    );
  };

  return (
    <div className="browse-page-container">
      <div className="browse-page-header">
        <h1 className="browse-page-title">Browse by Topic</h1>
        <p className="browse-page-subtitle">
          Explore skills grouped by topic, then start a practice session.
        </p>
        <div className="browse-search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="search"
            className="search-input"
            placeholder="Search topics…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search topics"
          />
          {search && (
            <button
              type="button"
              className="search-clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="browse-loading">
          <div className="browse-loading-spinner" />
          <span>Loading topics…</span>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {!loading && !error && filteredTopics.length === 0 && (
        <div className="browse-empty">
          {search ? `No topics match "${search}"` : 'No topics found.'}
        </div>
      )}

      {!loading && !error && (
        <div className="topics-list">
          {filteredTopics.map((topic) => {
            const count = topic.questionCount || 0;
            return (
              <section key={`${topic.state}-${topic.gradeLevel}-${topic.name}`} className="topic-section">
                <div className="topic-section-header">
                  <div className="topic-section-toggle">
                    <span className="topic-section-name">{topic.name}</span>
                    <span className="topic-section-meta">
                      <span className="topic-grade-badge">
                        {topic.state ? `${topic.state} · ${topic.gradeLevel}` : topic.gradeLevel}
                      </span>
                      <span className="topic-question-count">
                        {count} question{count !== 1 ? 's' : ''}
                      </span>
                    </span>
                  </div>
                  <div className="topic-section-source-btns">
                    <button
                      type="button"
                      className="primary topic-section-practice-btn"
                      onClick={() => handlePracticeClick(topic)}
                      disabled={!count}
                      title={`Practice ${topic.name}`}
                    >
                      Practice
                    </button>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TopicsPage;
